import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/auth-store'
import { useCustomerStore } from '@/stores/customer-store'
import { useDevicesStore } from '@/stores/devices-store'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

interface FormValues {
  restaurantUrl: string
  restaurantUrlSpoken: string
  labelEmail: string
  labelEmailSpoken: string
}

const schema = yup.object({
  restaurantUrl: yup
    .string()
    .trim()
    .required('Dieses Feld ist erforderlich')
    .url('Bitte geben Sie eine gültige URL ein'),
  restaurantUrlSpoken: yup
    .string()
    .trim()
    .required('Dieses Feld ist erforderlich'),
  labelEmail: yup
    .string()
    .trim()
    .required('E-Mail ist erforderlich')
    .matches(
      /^[\wäöüÄÖÜß.-]+@[a-zA-ZäöüÄÖÜß.-]+\.[a-zA-Z]{2,}$/,
      'Ungültige E-Mail Adresse'
    ),
  labelEmailSpoken: yup
    .string()
    .trim()
    .required('Dieses Feld ist erforderlich'),
})

export function RestaurantVariables() {
  const { t } = useTranslation()
  const { currentDevice, updateConfigData, getData } = useDevicesStore()
  const { canEdit } = useAuthStore()
  const { reset: resetCustomer } = useCustomerStore()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      restaurantUrl: '',
      restaurantUrlSpoken: '',
      labelEmail: '',
      labelEmailSpoken: '',
    },
  })

  useEffect(() => {
    if (currentDevice?.deviceId) {
      loadData()
    }
  }, [currentDevice?.deviceId])

  const loadData = () => {
    if (!currentDevice || !currentDevice.deviceConfiguration) {
      console.log('❌ RestaurantVariables: No device or config')
      return
    }

    reset({
      restaurantUrl: String(getData('HOMEPAGE_URL') || ''),
      restaurantUrlSpoken: String(getData('HOMEPAGE_URL_SPEAKABLE') || ''),
      labelEmail: String(getData('EMAIL') || ''),
      labelEmailSpoken: String(getData('EMAIL_SPEAKABLE') || ''),
    })
  }

  const onSubmit = async (data: FormValues) => {
    if (!isDirty) return

    setIsLoading(true)

    try {
      await Promise.all([
        updateConfigData('HOMEPAGE_URL', data.restaurantUrl),
        updateConfigData('HOMEPAGE_URL_SPEAKABLE', data.restaurantUrlSpoken),
        updateConfigData('EMAIL', data.labelEmail),
        updateConfigData('EMAIL_SPEAKABLE', data.labelEmailSpoken),
      ])

      await resetCustomer()

      toast({
        title: t('success.title'),
        description: t('restaurantVariables.saveSuccess'),
      })

      reset(data)
    } catch (error) {
      console.error('Error saving variables:', error)
      toast({
        variant: 'destructive',
        title: t('error.title'),
        description: t('restaurantVariables.saveError'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isDisabled = !canEdit() || isLoading

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Two Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Restaurant URL */}
          <div className="space-y-2">
            <Label htmlFor="restaurantUrl" className="text-sm font-semibold text-gray-700">
              {t('configuration.device.variables.labelUrl')}
            </Label>
            <Input
              id="restaurantUrl"
              {...register('restaurantUrl')}
              placeholder="https://example.com"
              disabled={isDisabled}
              className={errors.restaurantUrl ? 'border-red-300' : ''}
            />
            {errors.restaurantUrl && (
              <p className="text-sm text-red-600 font-medium">
                {errors.restaurantUrl.message}
              </p>
            )}
          </div>

          {/* Restaurant URL Spoken */}
          <div className="space-y-2">
            <Label htmlFor="restaurantUrlSpoken" className="text-sm font-semibold text-gray-700">
              {t('configuration.device.variables.labelUrlSpoken')}
            </Label>
            <Input
              id="restaurantUrlSpoken"
              {...register('restaurantUrlSpoken')}
              placeholder={t('restaurantVariables.urlSpokenPlaceholder')}
              disabled={isDisabled}
              className={errors.restaurantUrlSpoken ? 'border-red-300' : ''}
            />
            {errors.restaurantUrlSpoken && (
              <p className="text-sm text-red-600 font-medium">
                {errors.restaurantUrlSpoken.message}
              </p>
            )}
            <p className="text-xs text-gray-500">
              {t('restaurantVariables.urlSpokenHint')}
            </p>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="labelEmail" className="text-sm font-semibold text-gray-700">
              {t('configuration.device.variables.labelEmail')}
            </Label>
            <Input
              id="labelEmail"
              {...register('labelEmail')}
              placeholder="info@example.com"
              type="email"
              disabled={isDisabled}
              className={errors.labelEmail ? 'border-red-300' : ''}
            />
            {errors.labelEmail && (
              <p className="text-sm text-red-600 font-medium">
                {errors.labelEmail.message}
              </p>
            )}
          </div>

          {/* Email Spoken */}
          <div className="space-y-2">
            <Label htmlFor="labelEmailSpoken" className="text-sm font-semibold text-gray-700">
              {t('configuration.device.variables.labelEmailSpoken')}
            </Label>
            <Input
              id="labelEmailSpoken"
              {...register('labelEmailSpoken')}
              placeholder={t('restaurantVariables.emailSpokenPlaceholder')}
              disabled={isDisabled}
              className={errors.labelEmailSpoken ? 'border-red-300' : ''}
            />
            {errors.labelEmailSpoken && (
              <p className="text-sm text-red-600 font-medium">
                {errors.labelEmailSpoken.message}
              </p>
            )}
            <p className="text-xs text-gray-500">
              {t('restaurantVariables.emailSpokenHint')}
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            disabled={!isDirty || isDisabled}
            className="inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            {isLoading ? t('global.button.loading') : t('global.button.saveChanges')}
          </Button>
        </div>
      </form>
    </div>
  )
}
